import { textSm, textXs } from '@insify/design-system/typography';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAssistantChat } from '../../../api/getChat';
import { useChatStore } from '../../../stores/chatStore';

import { SendButton } from './send-button';
import styles from './styles.module.css';

import type { ChangeEvent, FC, FormEvent, KeyboardEvent } from 'react';

interface TextInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onSend?: (message: string) => void;
  disabled?: boolean;
}

interface UserInputProps {
  onInputChanged: (value: string) => void;
}

const UserInput: React.FC<UserInputProps> = ({ onInputChanged }) => {
  const { t } = useTranslation('assistant');
  const addUserMessage = useChatStore.use.sendMessage();

  const { mutate: sendMessage, isIdle, isSuccess, isError, reset } = useAssistantChat();

  const [input, setInput] = useState('');

  useEffect(() => {
    if (isSuccess) {
      setTimeout(() => {
        reset();
      }, 2000);
    }
  }, [isSuccess, reset]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onMessageSend(input);
  };

  const onInputChange = (value: string) => {
    onInputChanged(value);
    setInput(value);
  };

  const onMessageSend = (message: string) => {
    if (!message.trim()) return;
    sendMessage({ message });
    addUserMessage(message);
    setInput('');
  };

  return (
    <div className={styles.container}>
      <form className={styles.inputContainer} onSubmit={handleSubmit}>
        <TextInput value={input} onChange={onInputChange} onSend={onMessageSend} />
        <div className={styles.controls}>
          {/* <IconicMicrophone /> */}
          <SendButton
            disabled={input.trim() === '' && !isSuccess && !isError}
            className={styles.sendButton}
            onClick={handleSubmit}
            state={isIdle ? 'idle' : isSuccess ? 'success' : isError ? 'error' : 'loading'}
          />
        </div>
      </form>
      <p className={clsx(textXs, styles.inputHint)}>{t('disclaimer')}</p>
    </div>
  );
};

const TextInput: FC<TextInputProps> = ({ value, onChange, onSend }) => {
  const { t } = useTranslation('assistant');
  const [internalValue, setInternalValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isControlled = value !== undefined && onChange !== undefined;
  const inputValue = isControlled ? value : internalValue;

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      if (isControlled) {
        onChange?.(e.target.value);
      } else {
        setInternalValue(e.target.value);
      }
    },
    [isControlled, onChange, setInternalValue],
  );

  const handleSend = () => {
    const message = inputValue?.trim();
    if (message && onSend) {
      onSend(message);
      if (!isControlled) setInternalValue('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    resizeTextarea();
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder={t('text_input_placeholder')}
      className={clsx(textSm, styles.input)}
      style={{ resize: 'none', minHeight: '1rem', maxHeight: '20rem' }}
      aria-label={t('text_input_placeholder')}
    />
  );
};

export { UserInput };
